import { completeName, formatDateTime, i18n, millisecondsToTime } from '../../utils';
import { eventStateLabel } from 'utils/eventStateLabel';

export const normalizeDataDeviceAction = (payload: any) => {
  const data = payload?.data?.item?.items;
  if (payload?.data != null) {
    return data.map((item: any) => ({
      patientId: item.patientId,
      date: item.eventTimestamp,
      user: `${item.patientName.firstName !== null ? item.patientName.firstName : ''} ${item.patientName.middleName !== null ? item.patientName.middleName : ''} ${item.patientName.lastName !== null ? item.patientName.lastName : ''}`,
      firstName: item.patientName.firstName !== null ? item.patientName.firstName : '',
      middleName: item.patientName.middleName !== null ? item.patientName.middleName : '',
      lastName: item.patientName.lastName !== null ? item.patientName.lastName : '',
      actionType: i18n(item.eventType),
      alarmState: eventStateLabel(item),
      rawActionType: item.eventType,
      patientServiceNumber: item.patientServiceNumber,
      deviceActionId: item.eventId,
      deviceId: item.deviceId,
      deviceTypeCode: item.deviceTypeCode,
      devicePeripheralId: item.devicePeripheralId,
      devicePeripheralTypeCode: item.devicePeripheralTypeCode,
      patientGroup: item.patientGroup,
      group: item.patientGroup.code,
      lastDeviceLocation: item.lastDeviceLocation,
      resolved: !!item.resolution.resolution.id,
      resolutionId: item.resolution.resolution.id,
      resolutionTimestamp: item.resolution.timestamp,
      measureRawId: item.measureRawId,
      reference: item.reference,
      applicantName: item.resolution.applicantName,
      expedientId: item.expedientId,
    }));
  } else return null;
};

export const normalizeGeneralData = (payload: any, serviceStateLookup: Record<string, string> = {}) => {
  let data = {};
  if (payload != null) {
    data = {
      ...payload,
      patients: normalizeDataPatients(payload.patients, serviceStateLookup),
    };
    return data;
  }
};

export const normalizeDataPatients = (data: any, serviceStateLookup: Record<string, string> = {}) => {
  return data.map((item: any) => ({
    id: item.id,
    name: completeName(item.firstName, item.middleName, item.lastName),
    serviceNumber: item.serviceNumber,
    serviceStateCode: item.serviceState.code ? (serviceStateLookup[item.serviceState.code] ?? item.serviceState.code) : '',
    access: i18n(item.access),
    accessValue: item.access,
    group: item.group.code,
    actions: true,
  }));
};

export const normalizeStatusData = (payload: any) => {

  let data = {};

  if (payload.errorCode === 102) {
    data = { error: i18n('device.status.not.found') }
  } else {

    let status = payload.item;

    if (status != null) {

      if (
        status.bluetoothEnabled !== undefined ||
        status.bluetoothConnected !== undefined ||
        status.lastConnection !== undefined ||
        status.sendTimestamp !== undefined ||
        status.wearing !== undefined ||
        // Por el sello y no por `charging`: el status del hub Climax Lite ya trae una clave `charging`
        // (la lee la última rama de abajo) y no es un periférico.
        status.chargingTimestamp !== undefined
      ) {
        // Periférico: estado simplificado. Sólo dispone de estos campos.
        data = {
          kind: 'peripheral',
          peripheral: {
            bluetoothEnabled: status.bluetoothEnabled,
            // Tri-estado: null/undefined es "no lo reporta todavía", no "desconectado".
            bluetoothConnected: status.bluetoothConnected ?? null,
            battery: status.battery ?? null,
            firmwareVersion: checkStringEmptyOrUnknown(status.firmwareVersion),
            sendTimestamp:
              checkNumberEmptyOrUnknown(status.sendTimestamp) === i18n('text.field.UNKNOWN')
                ? i18n('text.field.UNKNOWN')
                : formatDateTime(status.sendTimestamp),
            // Último instante con enlace: el CarePoint envía status al caerse la conexión,
            // así que con bluetoothConnected a false es el momento de la desconexión.
            lastConnection:
              checkNumberEmptyOrUnknown(status.lastConnection) === i18n('text.field.UNKNOWN')
                ? i18n('text.field.UNKNOWN')
                : formatDateTime(status.lastConnection),
            // Tri-estado: null/undefined es "el periférico no lo reporta", no "no puesto".
            wearing: status.wearing ?? null,
            // Instante del cambio, sellado por el periférico: puede ser bastante anterior a
            // sendTimestamp (el aviso sale cuando hay enlace, el cambio ocurrió cuando ocurrió).
            wearingTimestamp:
              checkNumberEmptyOrUnknown(status.wearingTimestamp) === i18n('text.field.UNKNOWN')
                ? i18n('text.field.UNKNOWN')
                : formatDateTime(status.wearingTimestamp),
            // Mismo tri-estado y mismo sello del periférico que el wear.
            charging: status.charging ?? null,
            chargingTimestamp:
              checkNumberEmptyOrUnknown(status.chargingTimestamp) === i18n('text.field.UNKNOWN')
                ? i18n('text.field.UNKNOWN')
                : formatDateTime(status.chargingTimestamp),
          },
        };
      } else if (status.device_type) {
        data = {
          statusVersion: checkStringEmptyOrUnknown(''),
          zoommarine: {
            binary: {
              binaryName: checkStringEmptyOrUnknown(''),
              version: checkStringEmptyOrUnknown(status.client_version),
              core: checkStringEmptyOrUnknown(''),
            },
            program: {
              programName: checkStringEmptyOrUnknown(''),
            },
            solution: {
              solutionName: checkStringEmptyOrUnknown(''),
              programVersion: checkStringEmptyOrUnknown(''),
              resourceVersion: checkStringEmptyOrUnknown(''),
            },
          },
          telemedcare: {
            deviceType: checkStringEmptyOrUnknown(status.device_type),
            serviceNumbers: {
              userServiceNumber: checkStringEmptyOrUnknown(''),
              centralServiceNumbers: {
                incomingNumbersList: checkStringEmptyOrUnknown(''),
                outgoingNumbersList: checkStringEmptyOrUnknown(''),
                callbackNumbersList: checkStringEmptyOrUnknown(''),
              },
            },
          },
          device: {
            brand: checkStringEmptyOrUnknown(status.manufacturer_name),
            model: checkStringEmptyOrUnknown(status.model_name),
            startupTimestamp: checkStringEmptyOrUnknown(status.startup_time) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.startup_time),
            deviceTimestamp: checkStringEmptyOrUnknown(status.utc_timestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.utc_timestamp),
            operatingSystem: {
              operatingSystemName: checkStringEmptyOrUnknown(''),
              version: checkStringEmptyOrUnknown(status.os_version),
              label: checkStringEmptyOrUnknown(''),
              language: checkStringEmptyOrUnknown(''),
            },
            battery: {
              level: checkStringEmptyOrUnknown(status?.battery?.level),
              status: checkStringEmptyOrUnknown(status?.battery?.status),
              lowPowerMode: checkStringEmptyOrUnknown('') === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : '',
            },
            networks: {
              cellular: {
                provider: checkStringEmptyOrUnknown(''),
                technology: checkStringEmptyOrUnknown(status.mobile.network_type),
                protocol: checkStringEmptyOrUnknown(''),
                signalStrength: checkStringEmptyOrUnknown(status.mobile.signal_level),
                signalLevel: checkStringEmptyOrUnknown(''),
              },
              wifi: {
                ssid: checkStringEmptyOrUnknown(status?.wireless?.ssid),
                signalStrength: checkStringEmptyOrUnknown(status?.wireless?.signal_level),
                signalLevel: checkStringEmptyOrUnknown(''),
              },
              gps: {
                provider: checkStringEmptyOrUnknown(status.gps_position?.provider),
                latitude: checkStringEmptyOrUnknown(status.gps_position?.latitude),
                longitude: checkStringEmptyOrUnknown(status.gps_position?.longitude),
                obtainedTimestamp: checkStringEmptyOrUnknown('') === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN'): formatDateTime(''),
                accuracy: checkStringEmptyOrUnknown(status.gps_position?.accuracy),
              },
            },
          },
        };
      } else if (status.deviceTypeCode) {
        data = {
          statusVersion: checkStringEmptyOrUnknown(''),
          zoommarine: {
            binary: {
              binaryName: checkStringEmptyOrUnknown(''),
              version: checkStringEmptyOrUnknown(status.client_version),
              core: checkStringEmptyOrUnknown(''),
            },
            program: {
              programName: checkStringEmptyOrUnknown(''),
            },
            solution: {
              solutionName: checkStringEmptyOrUnknown(''),
              programVersion: checkStringEmptyOrUnknown(''),
              resourceVersion: checkStringEmptyOrUnknown(''),
            },
          },
          telemedcare: {
            deviceType: checkStringEmptyOrUnknown(status.deviceTypeCode),
            serviceNumbers: {
              userServiceNumber: checkStringEmptyOrUnknown(''),
              centralServiceNumbers: {
                incomingNumbersList: checkStringEmptyOrUnknown(''),
                outgoingNumbersList: checkStringEmptyOrUnknown(''),
                callbackNumbersList: checkStringEmptyOrUnknown(''),
              },
            },
          },
          device: {
            brand: checkStringEmptyOrUnknown(status.manufacturer_name),
            model: checkStringEmptyOrUnknown(status.model_name),
            startupTimestamp: checkStringEmptyOrUnknown(status.startupTime) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(payload.startupTime),
            deviceTimestamp: checkStringEmptyOrUnknown(status.utcTimestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(payload.utcTimestamp),
            operatingSystem: {
              operatingSystemName: checkStringEmptyOrUnknown(''),
              version: checkStringEmptyOrUnknown(status.os_version),
              label: checkStringEmptyOrUnknown(''),
              language: checkStringEmptyOrUnknown(''),
            },
            battery: {
              level: checkStringEmptyOrUnknown(status?.battery?.level),
              status: checkStringEmptyOrUnknown(status?.battery?.batteryState),
              lowPowerMode: checkStringEmptyOrUnknown(status.battery?.lowPowerMode) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : payload.battery?.lowPowerMode.toString(),
            },
            networks: {
              cellular: {
                provider: checkStringEmptyOrUnknown(status.mobile?.carrier),
                technology: checkStringEmptyOrUnknown(status.mobile?.networkType),
                protocol: checkStringEmptyOrUnknown(''),
                signalStrength: checkStringEmptyOrUnknown(status.mobile.signalLevel),
                signalLevel: checkStringEmptyOrUnknown(''),
              },
              wifi: {
                ssid: checkStringEmptyOrUnknown(status?.wireless?.ssid),
                signalStrength: checkStringEmptyOrUnknown(status?.wireless?.signalLevel),
                signalLevel: checkStringEmptyOrUnknown(''),
              },
              gps: {
                provider: checkStringEmptyOrUnknown(status.network),
                latitude: checkStringEmptyOrUnknown(status.gpsPosition?.latitude),
                longitude: checkStringEmptyOrUnknown(status.gpsPosition?.longitude),
                obtainedTimestamp: checkStringEmptyOrUnknown('') === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN'): formatDateTime(''),
                accuracy: checkStringEmptyOrUnknown(status.gpsPosition?.accuracy),
              },
            },
          },
        };
      } else if (status.statusVersion === 2) {
        data = {
          statusVersion: status.statusVersion,
          zoommarine: {
            binary: {
              binaryName: checkStringEmptyOrUnknown(status.zoommarine?.binary?.name),
              version: checkStringEmptyOrUnknown(status.zoommarine?.binary?.version),
              core: checkStringEmptyOrUnknown(status.zoommarine?.binary?.core),
            },
            program: {
              programName: checkStringEmptyOrUnknown(status.zoommarine?.program?.name),
            },
            solution: {
              solutionName: checkStringEmptyOrUnknown(status.zoommarine?.solution?.name),
              programVersion: checkStringEmptyOrUnknown(status.zoommarine?.solution?.programVersion),
              resourceVersion: checkStringEmptyOrUnknown(status.zoommarine?.solution?.resourceVersion),
            },
          },
          telemedcare: {
            deviceType: checkStringEmptyOrUnknown(status.telemedcare?.deviceType),
            serviceNumbers: {
              userServiceNumber: checkStringEmptyOrUnknown(status.telemedcare?.serviceNumbers?.userServiceNumber),
              centralServiceNumbers: {
                incomingNumbersList: checkStringEmptyOrUnknown(status.telemedcare?.serviceNumbers?.centralServiceNumbers?.incomingNumbersList),
                outgoingNumbersList: checkStringEmptyOrUnknown(status.telemedcare?.serviceNumbers?.centralServiceNumbers?.outgoingNumbersList),
                callbackNumbersList: checkStringEmptyOrUnknown(status.telemedcare?.serviceNumbers?.centralServiceNumbers?.callbackNumbersList),
              },
            },
          },
          device: {
            brand: checkStringEmptyOrUnknown(status.device?.brand),
            model: checkStringEmptyOrUnknown(status.device?.model),
            firmwareVersion: checkStringEmptyOrUnknown(status.device?.firmwareVersion),
            statusPeriodicity: checkNumberEmptyOrUnknown(status.statusPeriodicity) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : millisecondsToTime(status.statusPeriodicity),
            startupTimestamp: checkStringEmptyOrUnknown(status.device?.startupTimestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.device?.startupTimestamp),
            deviceTimestamp: checkStringEmptyOrUnknown(status.device?.deviceTimestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.device?.deviceTimestamp),
            operatingSystem: {
              operatingSystemName: checkStringEmptyOrUnknown(status.device?.operatingSystem?.name),
              version: checkStringEmptyOrUnknown(status.device?.operatingSystem?.version),
              label: checkStringEmptyOrUnknown(status.device?.operatingSystem?.label),
              language: checkStringEmptyOrUnknown(status.device?.operatingSystem?.language),
            },
            battery: {
              level: checkStringEmptyOrUnknown(status.device?.battery?.level),
              status: checkStringEmptyOrUnknown(status.device?.battery?.status),
              lowPowerMode: checkStringEmptyOrUnknown(status.device?.battery?.lowPowerMode) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : status.device?.battery?.lowPowerMode.toString(),
            },
            networks: {
              cellular: {
                provider: checkStringEmptyOrUnknown(status.device?.networks?.cellular?.provider),
                technology: checkStringEmptyOrUnknown(status.device?.networks?.cellular?.technology),
                protocol: checkStringEmptyOrUnknown(status.device?.networks?.cellular?.protocol),
                signalStrength: checkStringEmptyOrUnknown(status.device?.networks?.cellular?.signalStrength),
                signalLevel: checkStringEmptyOrUnknown(status.device?.networks?.cellular?.signalLevel) + signalLevelText(status.device?.networks?.cellular?.signalLevel),
              },
              wifi: {
                ssid: checkStringEmptyOrUnknown(status.device?.networks?.wifi?.ssid),
                signalStrength: checkStringEmptyOrUnknown(status.device?.networks?.wifi?.signalStrength),
                signalLevel: checkStringEmptyOrUnknown(status.device?.networks?.wifi?.signalLevel) + signalLevelText(status.device?.networks?.wifi?.signalLevel),
              },
              gps: {
                provider: checkStringEmptyOrUnknown(status.device?.networks?.gps?.provider),
                latitude: checkNumberEmptyOrUnknown(status.device?.networks?.gps?.latitude),
                longitude: checkNumberEmptyOrUnknown(status.device?.networks?.gps?.longitude),
                obtainedTimestamp: checkNumberEmptyOrUnknown(status.device?.networks?.gps?.obtainedTimestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.device?.networks?.gps?.obtainedTimestamp),
                accuracy: checkNumberEmptyOrUnknown(status.device?.networks?.gps?.accuracy),
              },
            },
          },
        };
      } else {

        data = {
          statusVersion: '',
          zoommarine: {
            binary: {
              binaryName: '',
              version: '',
              core: '',
            },
            program: {
              programName: '',
            },
            solution: {
              solutionName: '',
              programVersion: '',
              resourceVersion: '',
            },
          },
          telemedcare: {
            deviceType: '',
            serviceNumbers: {
              userServiceNumber: '',
              centralServiceNumbers: {
                incomingNumbersList: '',
                outgoingNumbersList: '',
                callbackNumbersList: '',
              },
            },
          },
          device: {
            brand: '',
            model: '',
            firmwareVersion: '',
            statusPeriodicity: '',
            startupTimestamp: '',
            deviceTimestamp: '',
            operatingSystem: {
              operatingSystemName: '',
              version: '',
              label: '',
              language: '',
            },
            battery: {
              level: checkStringEmptyOrUnknown(status.battery),
              status: status.charging ? 'Cargando' : 'Descargando',
              lowPowerMode: '',
            },
            networks: {
              cellular: {
                provider: '',
                technology: '',
                protocol: '',
                signalStrength: '',
                signalLevel: checkStringEmptyOrUnknown(status.mobileSignal),
              },
              wifi: {
                ssid: '',
                signalStrength: '',
                signalLevel: '',
              },
              gps: {
                provider: checkStringEmptyOrUnknown(status.isGpsOrWifiCoordinates),
                latitude: checkNumberEmptyOrUnknown(status.coordinates?.latitude),
                longitude: checkNumberEmptyOrUnknown(status.coordinates?.longitude),
                obtainedTimestamp: checkNumberEmptyOrUnknown(status.coordinates?.timestamp) === i18n('text.field.UNKNOWN') ? i18n('text.field.UNKNOWN') : formatDateTime(status.coordinates?.timestamp),
                accuracy: '',
              },
            },
          },
        };
      }
    } else data = '';
  }

  return data;
};

const checkStringEmptyOrUnknown = (value: any) => {

  if (value === undefined || value === '') {
    return i18n('text.field.UNKNOWN');
  } else {
    return value;
  }
};

const checkNumberEmptyOrUnknown = (value: any) => {

  if (value === undefined || value === 0) {
    return i18n('text.field.UNKNOWN');
  } else {
    return value;
  }
};

const signalLevelText = (value: any) => {

  if (value === 0) {
    return " - " + i18n('status.signal.level.0');
  } else if (value === 1) {
    return " - " + i18n('status.signal.level.1');
  } else if (value === 2) {
    return " - " + i18n('status.signal.level.2');
  } else if (value === 3) {
    return " - " + i18n('status.signal.level.3');
  } else if (value === 4) {
    return " - " + i18n('status.signal.level.4');
  } else {
    return ""
  }
};
